/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable @typescript-eslint/no-explicit-any */
import { inject, Injectable, signal } from '@angular/core';
import {
    OnsiteCheckInClient,
    OnsiteCheckInClientConfig,
} from '@azavista/planner-lib';
import { DatabaseState, SyncStatus } from '@azavista/frontend-cache-lib';
import { BehaviorSubject, filter, firstValueFrom, take } from 'rxjs';
import { NetworkStatus } from '@azavista/frontend-cache-lib/lib/network-manager/network-manager';
import { environment } from '../../../../environments/environment';
import { getMapByGetter } from '../../shared.service';
import {
    GetManyOption,
    OnsiteCheckInClientUtilService,
} from './onsite-checkin-client.util.service';
import { OnsiteCheckInClientErrorService } from './onsite-checkin-client-error.service';
import { ConfigurationStorageService } from '../../configuration-storage.service';
import { ReplaceFunctionParams } from '../../../settings/utils.types';
import { IAppConfig } from '../../interfaces';

type PartitionKeyEventId = string & {};
type PartitionKeyActivityId = string & {};
type PartitionKeyDefault = 'default';
type ClientCollectionPartitionKeyRecord = {
    eventsCollection: PartitionKeyEventId | PartitionKeyDefault;
    activitiesCollection: PartitionKeyEventId;
    activityConditionsCollection: PartitionKeyEventId;
    activityGroupsCollection: PartitionKeyEventId;
    activityParticipantsCollection: PartitionKeyActivityId;
    scanStagesCollection: PartitionKeyEventId;
    stagesCollection: PartitionKeyEventId;
    participantFieldsCollection: PartitionKeyEventId;
    multiregistrationsCollection: PartitionKeyEventId;
    workflowsCollection: PartitionKeyEventId;
    eventFieldsCollection: PartitionKeyEventId;
    badgesCollection: PartitionKeyEventId;
    eventBadgeFontsCollection: PartitionKeyEventId;
    eventDevicesCollection: PartitionKeyEventId;
    participantsCollection: PartitionKeyEventId;
    onsiteSettingCollection: PartitionKeyEventId;
    badgesWithTemplateCollection: PartitionKeyEventId;

    globalEventFieldsCollection: PartitionKeyDefault;
    globalSettingCollection: PartitionKeyDefault;
    parentSettingCollection: PartitionKeyDefault;
    organizationsCollection: PartitionKeyDefault;
    integrationsCollection: PartitionKeyDefault;
};
type ClientCollectionName = keyof ClientCollectionPartitionKeyRecord;
const clientCollectionMethodsWithPartitionKey = [
    'getOne',
    'getMany',
    'getStats',
    'onStatsChange',
] as const satisfies Array<keyof OnsiteCheckInClient[ClientCollectionName]>;
type ClientCollectionMethodsWithPartitionKey =
    (typeof clientCollectionMethodsWithPartitionKey)[number];

@Injectable({
    providedIn: 'root',
})
export class OnsiteCheckInClientService
    implements Record<keyof OnsiteCheckInClient & (`${string}Collection` | 'getFontFile'), object>
{
    isInit$ = new BehaviorSubject(false);
    isPrefetchingEvent$ = signal<boolean>(false);
    private client = inject(OnsiteCheckInClient);
    private clientUtilsSvc = inject(OnsiteCheckInClientUtilService);
    private clientErrorSvc = inject(OnsiteCheckInClientErrorService);
    private configService = inject(ConfigurationStorageService);

    private _clientConfig?: OnsiteCheckInClientConfig;
    public get clientConfig(): OnsiteCheckInClientConfig {
        return this._clientConfig;
    }

    status$ = new BehaviorSubject<DatabaseState>({
        networkStatus: navigator.onLine
            ? NetworkStatus.ONLINE
            : NetworkStatus.OFFLINE,
        syncState: {
            status: SyncStatus.SYNCED,
        },
    });
    private clientOnStateChangeUnsubscribe?: () => void;

    eventsCollection = this.getCustomClientCollection('eventsCollection');
    stagesCollection = this.getCustomClientCollection('stagesCollection');
    scanStagesCollection = this.getCustomClientCollection(
        'scanStagesCollection'
    );
    workflowsCollection = this.getCustomClientCollection('workflowsCollection');
    multiregistrationsCollection = this.getCustomClientCollection(
        'multiregistrationsCollection'
    );
    eventFieldsCollection = this.getCustomClientCollection(
        'eventFieldsCollection'
    );
    badgesCollection = this.getCustomClientCollection('badgesCollection');
    badgesWithTemplateCollection = this.getCustomClientCollection('badgesWithTemplateCollection');
    eventBadgeFontsCollection = this.getCustomClientCollection('eventBadgeFontsCollection');
    eventDevicesCollection = this.getCustomClientCollection('eventDevicesCollection');
    participantsCollection = this.getCustomClientCollection(
        'participantsCollection'
    );
    participantFieldsCollection = this.getCustomClientCollection(
        'participantFieldsCollection'
    );
    onsiteSettingCollection = this.getCustomClientCollection(
        'onsiteSettingCollection'
    );
    activitiesCollection = this.getCustomClientCollection(
        'activitiesCollection'
    );
    activityParticipantsCollection = this.getCustomClientCollection(
        'activityParticipantsCollection'
    );
    activityConditionsCollection = this.getCustomClientCollection(
        'activityConditionsCollection'
    );
    activityGroupsCollection = this.getCustomClientCollection(
        'activityGroupsCollection'
    );

    globalEventFieldsCollection = this.getCustomClientCollection(
        'globalEventFieldsCollection'
    );
    globalSettingCollection = this.getCustomClientCollection(
        'globalSettingCollection'
    );
    parentSettingCollection = this.getCustomClientCollection(
        'parentSettingCollection'
    );
    organizationsCollection = this.getCustomClientCollection(
        'organizationsCollection'
    );

    integrationsCollection = this.getCustomClientCollection(
        'integrationsCollection'
    );

    get printServerApi() {
        return this.client.printServerApi;
    }

    async init(config: OnsiteCheckInClientConfig) {
        this.isInit$.next(false);
        this.clientOnStateChangeUnsubscribe?.();
        const clientConfig: OnsiteCheckInClientConfig = {
            clientId: environment.clientId,
            printServerBaseUrl: this.configService.getAppConfig().printServerBaseUrl,
            ...config,
        };
        const appConfig = {
            apiServerBaseUrl: clientConfig.baseUrl,
            printServerBaseUrl: clientConfig.printServerBaseUrl,
        } satisfies Partial<IAppConfig>;
        this.configService.setAppConfig(appConfig);
        await this.client.init(clientConfig);
        if (new URLSearchParams(window.location.search).get('debug') === 'true') {
            this.client.enableDBDebugging();
        }
        this.printServerApi.setPrintServerBaseURL(clientConfig.printServerBaseUrl);
        if (!this.clientUtilsSvc.getIsHttpClientHasSameConfig(config, this.client.httpClient)) {
            const defaults = this.client.httpClient.defaults;
            defaults.baseURL = config.baseUrl;
            defaults.clientId = config.clientId;
        }
        this.isInit$.next(true);
        this._clientConfig = clientConfig;
        this.client.prefetchGlobals();
        this.clientOnStateChangeUnsubscribe = this.client.onStateChange(
            (state) => this.status$.next(state)
        );
    }

    destroy = () => {
        this.clientOnStateChangeUnsubscribe?.();
        this.client.destroy();
        this.isInit$.next(false);
    }

    async prefetchEvent(eventId: string) {
        this.isPrefetchingEvent$.set(true);
        return new Promise<void>((resolve) => {
            const executePrefetch = async () => {
                await this.client.prefetchEvent(eventId);
                this.isPrefetchingEvent$.set(false);
                resolve();
            };
            // Only execute prefetch once the `OnsiteCheckInClient.init()` completed
            if (this.isInit$.value) {
                executePrefetch();
            } else {
                this.isInit$
                    .pipe(
                        filter((v) => !!v),
                        take(1)
                    )
                    .subscribe(() => executePrefetch());
            }
        });
    }

    exitEvent(eventId: string) {
        this.client.exitEvent(eventId)
    }

    getFontFile(fontId: string): Promise<Blob> {
        return this.client.getFontFile(fontId);
    }

    getCustomClientCollection<
        CollectionName extends keyof OnsiteCheckInClient &
            `${string}Collection` &
            ClientCollectionName,
        PartitionKey extends ClientCollectionPartitionKeyRecord[CollectionName]
    >(collectionName: CollectionName) {
        const collection = this.client[collectionName];
        const getMethodFn = <M extends ClientCollectionMethodsWithPartitionKey>(
            methodName: M
        ) => {
            const methodFn = collection[methodName];
            const fn = this.awaitedMethod(
                methodFn.bind(collection) as typeof methodFn
            );
            return fn as unknown as ReplaceFunctionParams<
                typeof fn,
                (partitionKey: PartitionKey) => any
            >;
        };
        const methodWithPartition = {
            getOne: getMethodFn('getOne'),
            getMany: getMethodFn('getMany'),
            getStats: getMethodFn('getStats'),
            onStatsChange: getMethodFn('onStatsChange'),
        } satisfies Record<ClientCollectionMethodsWithPartitionKey, any>;

        return {
            ...methodWithPartition,
            /**
             * GetMany method that supports legacy requests and responses
             * @param partitionKey
             * @param req
             * @param options
             * @returns
             */
            getManyWithReq: <
                Req extends Parameters<
                    OnsiteCheckInClientUtilService['collectionRequestFnRecord'][CollectionName]
                >[0]
            >(
                partitionKey: PartitionKey,
                req: Req,
                options?: GetManyOption
            ) =>
                this.clientUtilsSvc.getMany(
                    collectionName,
                    partitionKey,
                    req,
                    options
                ),
            getAllWithReq: async <
                Req extends Parameters<
                    OnsiteCheckInClientUtilService['collectionRequestFnRecord'][CollectionName]
                >[0]
            >(
                partitionKey: PartitionKey,
                req: Omit<Req, 'offset' | 'limit'>,
                options?: GetManyOption
            ) => {
                const rows =
                    await this.clientUtilsSvc.getAllPagedItems(
                        paging => this.clientUtilsSvc.getMany(
                            collectionName,
                            partitionKey,
                            {...req || {}, ...paging, },
                            options
                        ),
                        res => res.data
                    )
                return rows;
            },
            mutations: this.getCollectionMutationMethods(collectionName),
            refetch: collection.refetch.bind(collection),
        };
    }

    getCollectionMutationMethods<
        CollectionName extends keyof OnsiteCheckInClient &
            `${string}Collection` &
            ClientCollectionName,
        MutationObj extends OnsiteCheckInClient[CollectionName]['mutations'],
        MutationKeys extends keyof MutationObj
    >(
        collectionName: CollectionName
    ): {
        [key in MutationKeys]: MutationObj[key] extends (...params) => any
            ? MutationObj[key]
            : undefined;
    } {
        const mutations = this.client[collectionName].mutations;
        type MutationName = keyof typeof mutations;

        return getMapByGetter(
            Object.keys(mutations) as Array<MutationName>,
            (key: MutationName) => key,
            (key: MutationName) => {
                const collection = this.client[collectionName];
                const methodFn = collection.mutations[key];
                return this.awaitedMethod(
                    (methodFn as any)?.bind(collection) as typeof methodFn
                );
            }
        ) as any;
    }

    awaitedMethod<
        Method extends (...params) => any,
        MethodReturn extends ReturnType<Method>
    >(
        method: Method
    ): (
        ...params: Parameters<Method>
    ) => MethodReturn extends Promise<any>
        ? MethodReturn
        : Promise<MethodReturn> {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        return async (...params: Parameters<Method>) => {
            try {
                await this.awaitInitCompleted();
                return method(...params).catch(error => {
                    this.clientErrorSvc.setError(error);
                });
            } catch (error) {
                this.clientErrorSvc.setError(error);
            }
        };
    }

    async awaitInitCompleted() {
        if (!this.isInit$.value) {
            await firstValueFrom(
                this.isInit$.pipe(filter((isInit) => !!isInit))
            );
        }
    }
}
